/**
 * Badge — a status pill.
 *
 * Colour is never the only signal: a `changes_requested` course and a
 * `published` one must be distinguishable by their text, because roughly one man
 * in twelve cannot separate the red from the green (NFR-A11y).
 */
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from './cn'

const badge = cva(
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap',
  {
    variants: {
      tone: {
        neutral: 'bg-sunken text-fg-muted ring-1 ring-line',
        // ⚠️ No `dark:` pair any more. The old one borrowed `info` for dark mode
        // because the placeholder's dark tints did not carry text; info is a
        // fixed blue now, so that pair would have turned every brand badge blue
        // at night. The tints are real dark wine grounds instead, and
        // `brand-fg` measures 6.90:1 on `brand-50` there (9.00:1 in light).
        brand: 'bg-brand-50 text-brand-fg ring-1 ring-brand-100',
        success: 'bg-success-soft text-success ring-1 ring-success/25',
        warning: 'bg-warning-soft text-warning ring-1 ring-warning/25',
        danger: 'bg-danger-soft text-danger ring-1 ring-danger/25',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
)

export type BadgeTone = NonNullable<VariantProps<typeof badge>['tone']>

export type BadgeProps = React.ComponentPropsWithoutRef<'span'> & VariantProps<typeof badge>

export function Badge({ className, tone, ...props }: BadgeProps) {
  return <span className={cn(badge({ tone }), className)} {...props} />
}
